/**
 * API Configuration with BrandManager
 *
 * Uses brandManager.apiUrl for hostname-based API endpoint detection.
 * The axios client base URL is updated when settings bootstrap calls updateFromApi.
 */

import axios from 'axios';
import brandManager from '../services/brandManager';

const API_BASE_URL = brandManager.apiUrl;

if (process.env.NODE_ENV === 'development') {
  console.log('🌐 API Configuration:');
  console.log('Current URL:', typeof window !== 'undefined' ? window.location.origin : 'Server-side');
  console.log('API Base URL:', API_BASE_URL);
}

export const apiClient = axios.create({
    baseURL: API_BASE_URL,
    timeout: 30000,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'platform': 'qr',
    },
    withCredentials: false,
});

export function updateApiClientBaseUrl(): void {
  apiClient.defaults.baseURL = brandManager.apiUrl;
}

// Default values for API requests
export const DEFAULT_BRANCH_ID = 1;
export const DEFAULT_TYPE_ID = 1;

// Request interceptor to add auth token and language
apiClient.interceptors.request.use(
    (config) => {
        // Add auth token if available
        const token = localStorage.getItem('auth_token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        // Add language header - read fresh from localStorage each time
        const getLanguage = () => {
            // Try multiple sources for language
            const lang = localStorage.getItem('language') || 
                        localStorage.getItem('i18nextLng') || 
                        document.documentElement.lang ||
                        'ar';
            return lang === 'ar' ? 'ar' : 'en';
        };
        
        const finalLang = getLanguage();
        config.headers.lang = finalLang;
        
        // Ensure platform header is set
        config.headers.platform = 'qr';

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
    (response) => response,
    (error) => {
        // Handle 401 unauthorized errors
        if (error.response?.status === 401) {
            // Clear auth data
            localStorage.removeItem('auth_token');
            localStorage.removeItem('phone_number');
            // Optionally redirect to login or dispatch logout action
        }
        
        // Handle 403 forbidden errors (session expired)
        if (error.response?.status === 403) {
            // Clear auth data as session may have expired
            localStorage.removeItem('auth_token');
            localStorage.removeItem('phone_number');
        }
        
        // Handle has_no_open_session error specifically
        if (error.response?.data?.descriptiveMessage === 'has_no_open_session') {
            // Clear auth data as session has expired
            localStorage.removeItem('auth_token');
            localStorage.removeItem('phone_number');
        }

        // Handle CORS errors
        if (error.message === 'Network Error' || !error.response) {
            // Network/CORS Error detected
        }

        return Promise.reject(error);
    }
);

export default apiClient;
